import { can } from '../utils/permissions';
import React, { useState, useEffect, useCallback } from 'react';
import Sidebar from './Sidebar';
import attendanceService from '../services/attendanceService';
import './AttendanceScreen.css';

/* ─── Normalize API response → UI shape ─── */
const normalizeStudent = (s) => ({
  id:         s.admissionNumber || String(s.studentId),
  studentId:  s.studentId,
  name:       s.studentName,
  tower:      s.tower || '—',
  status:     s.currentStatus === 'IN' ? 'in' : s.currentStatus === 'ON_LEAVE' ? 'leave' : 'out',
  lastPunch:  s.lastPunchType || '—',
  lastTime:   s.lastPunchTime || '—',
  attendance: s.attendancePct || 0,
  alert:      s.lowAttendanceAlert || false,
  leaveFlag:  s.hasLeave || false,
  leaveInfo:  s.leaveInfo || null,
  punches:    (s.todayPunches || []).map(p => ({
    type: p.punchType === 'IN' ? 'in' : 'out',
    time: p.punchTime ? p.punchTime.split(', ')[1] || p.punchTime : '—',
    seq:  p.sequenceNo,
  })),
  thisWeek:   [],
});

const normalizeLeave = (l) => ({
  id:           l.id,
  studentId:    l.studentId,
  name:         l.studentName,
  tower:        l.tower || '—',
  applied:      l.appliedAt || '—',
  from:         l.fromDate,
  to:           l.toDate,
  days:         l.days,
  type:         l.leaveType ? l.leaveType.replace(/_/g, ' ').replace(/\w/g, c => c.toUpperCase()) : '—',
  reason:       l.reason || '',
  status:       (l.status || 'PENDING').toLowerCase(),
  parentStatus: l.parentNotifStatus === 'SENT' ? 'sent'
              : l.parentNotifStatus === 'PENDING' ? 'awaiting' : 'notified',
});

/* ─── Helpers ─── */
const Avatar = ({ name, size = 34 }) => (
  <div style={{
    width: size, height: size, borderRadius: '50%',
    background: `hsl(${(name?.charCodeAt(0) || 0) * 15 % 360},45%,55%)`,
    color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: size * 0.4, fontWeight: 700, flexShrink: 0,
    border: '2px solid #E8E4DF',
  }}>
    {name?.[0]?.toUpperCase() || '?'}
  </div>
);

const StatusBadge = ({ status }) => {
  if (status === 'in')    return <span className="att-badge att-badge-in"><span className="att-badge-dot" />In Premises</span>;
  if (status === 'out')   return <span className="att-badge att-badge-out"><span className="att-badge-dot" />Outside</span>;
  if (status === 'leave') return <span className="att-badge att-badge-leave"><span className="att-badge-dot" />On Leave</span>;
  return null;
};

const PunchBadge = ({ punch }) => {
  if (punch === 'IN')  return <span className="att-badge att-badge-in"><span className="att-badge-dot" />Swipe IN</span>;
  if (punch === 'OUT') return <span className="att-badge att-badge-out"><span className="att-badge-dot" />Swipe OUT</span>;
  return <span style={{ color: 'var(--att-muted)' }}>—</span>;
};

/* ════════════════════════════════════════════════════ */
const AttendanceScreen = ({ onNavigate, onLogout }) => {
  const [activeTab, setActiveTab]       = useState('today');
  const [students,  setStudents]        = useState([]);
  const [leaves,    setLeaves]          = useState([]);
  const [overview,  setOverview]        = useState({ total:0, inPremises:0, outside:0, onLeave:0, pendingLeave:0 });
  const [bioLog,    setBioLog]          = useState([]);
  const [monthlyReport, setMonthlyReport] = useState([]);
  const [loading,   setLoading]         = useState(false);
  const [search,    setSearch]          = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [towerFilter,  setTowerFilter]  = useState('');
  const [bioDate,   setBioDate]         = useState(new Date().toISOString().slice(0,10));
  const [reportMonth, setReportMonth]   = useState(new Date().toISOString().slice(0,7));
  const [drawer,    setDrawer]          = useState(null);
  const [leaveModal, setLeaveModal]     = useState(false);
  const [leaveForm, setLeaveForm]       = useState({ studentId:'', studentName:'', leaveType:'HOME_LEAVE', from:'', to:'', reason:'', destination:'', contact:'' });
  const [studentSearch, setStudentSearch] = useState('');
  const [studentSuggestions, setStudentSuggestions] = useState([]);

  /* ── Load today data ── */
  const loadToday = useCallback(async () => {
    setLoading(true);
    try {
      const [ov, list] = await Promise.all([
        attendanceService.getTodayOverview().catch(() => null),
        attendanceService.getTodayStatus(search, statusFilter ? statusFilter.toUpperCase() : '', towerFilter).catch(() => []),
      ]);
      if (ov) setOverview(ov);
      setStudents((list || []).map(normalizeStudent));
    } catch(e) { console.error('Failed to load today status:', e); }
    finally { setLoading(false); }
  }, [search, statusFilter, towerFilter]);

  /* ── Load biometric log ── */
  const loadBioLog = useCallback(async () => {
    setLoading(true);
    try {
      const data = await attendanceService.getBiometricLog(bioDate).catch(() => []);
      setBioLog(data || []);
    } catch(e) { console.error('Failed to load bio log:', e); }
    finally { setLoading(false); }
  }, [bioDate]);

  /* ── Load leaves ── */
  const loadLeaves = useCallback(async () => {
    setLoading(true);
    try {
      const data = await attendanceService.getAllLeaves().catch(() => []);
      setLeaves((data || []).map(normalizeLeave));
    } catch(e) { console.error('Failed to load leaves:', e); }
    finally { setLoading(false); }
  }, []);

  /* ── Load monthly report ── */
  const loadReport = useCallback(async () => {
    setLoading(true);
    try {
      const [y, m] = reportMonth.split('-').map(Number);
      const data = await attendanceService.getMonthlyReport(y, m).catch(() => []);
      setMonthlyReport(data || []);
    } catch(e) { console.error('Failed to load report:', e); }
    finally { setLoading(false); }
  }, [reportMonth]);

  /* ── Auto-load on tab change ── */
  useEffect(() => {
    if (activeTab === 'today')         { loadToday(); loadLeaves(); }
    if (activeTab === 'biometric')     loadBioLog();
    if (activeTab === 'leave')         loadLeaves();
    if (activeTab === 'report')        loadReport();
  }, [activeTab]);

  /* ── Reload today when filters change ── */
  useEffect(() => {
    if (activeTab === 'today') loadToday();
  }, [search, statusFilter, towerFilter]);

  /* ── Reload bio log when date changes ── */
  useEffect(() => {
    if (activeTab === 'biometric') loadBioLog();
  }, [bioDate]);

  /* ── Reload report when month changes ── */
  useEffect(() => {
    if (activeTab === 'report') loadReport();
  }, [reportMonth]);

  /* pending leave count */
  const pendingCount = leaves.filter(l => l.status === 'pending').length;

  /* ── Cross-reference: mark students as ON_LEAVE if approved leave exists for today ── */
  const today_str = new Date().toISOString().slice(0,10);
  const studentsWithLeave = students.map(s => {
    const activeLeave = leaves.find(l =>
      l.status === 'approved' &&
      String(l.studentId) === String(s.studentId) &&
      l.from <= today_str && l.to >= today_str
    );
    if (activeLeave) {
      return { ...s, status: 'leave', leaveFlag: true,
        leaveInfo: `Approved · ${activeLeave.from} – ${activeLeave.to}`,
        alert: false };
    }
    return s;
  });

  /* filtered table */
  const filtered = studentsWithLeave.filter(s =>
    (s.name.toLowerCase().includes(search.toLowerCase()) || (s.id||'').toLowerCase().includes(search.toLowerCase())) &&
    (!statusFilter || s.status === statusFilter.toLowerCase().replace('on_leave','leave')) &&
    (!towerFilter  || s.tower === towerFilter)
  );

  /* approve / reject leave */
  const updateLeave = async (id, newStatus) => {
    try {
      await (newStatus === 'approved'
        ? attendanceService.approveLeave(id)
        : attendanceService.rejectLeave(id));
      setLeaves(prev => prev.map(l => l.id === id ? { ...l, status: newStatus } : l));
    } catch(e) { console.error('Failed to update leave:', e); }
  };

  /* leave form submit */
  const submitLeave = async () => {
    if (!leaveForm.studentId) {
      alert('Please select a student from the list'); return;
    }
    if (!leaveForm.from || !leaveForm.to) {
      alert('Please select from and to dates'); return;
    }
    try {
      await attendanceService.applyLeave({
        studentId:          parseInt(leaveForm.studentId),
        leaveType:          leaveForm.leaveType,
        fromDate:           leaveForm.from,
        toDate:             leaveForm.to,
        reason:             leaveForm.reason,
        destination:        leaveForm.destination,
        contactDuringLeave: leaveForm.contact,
        notifyWhatsapp:     true,
        notifyEmail:        true,
        notifySms:          false,
      });
      setLeaveModal(false);
      setLeaveForm({ studentId:'', studentName:'', leaveType:'HOME_LEAVE', from:'', to:'', reason:'', destination:'', contact:'' });
      setStudentSearch('');
      setStudentSuggestions([]);
      loadLeaves();
    } catch(e) {
      alert('Failed to submit leave: ' + (e.response?.data?.message || e.message));
    }
  };

  /* ── Student search for leave form ── */
  const searchStudents = (query) => {
    setStudentSearch(query);
    if (query.length < 2) { setStudentSuggestions([]); return; }
    const matches = students.filter(s =>
      s.name.toLowerCase().includes(query.toLowerCase()) ||
      s.id.toLowerCase().includes(query.toLowerCase())
    ).slice(0, 6);
    setStudentSuggestions(matches);
  };

  const selectStudent = (s) => {
    setLeaveForm(p => ({ ...p, studentId: String(s.studentId), studentName: s.name }));
    setStudentSearch(s.name);
    setStudentSuggestions([]);
  };

  const today = new Date().toLocaleDateString('en-IN', { weekday:'long', day:'numeric', month:'short', year:'numeric' });

  return (
    <div className="app-layout">
      <Sidebar activeItem="attendance" onNavigate={onNavigate} onLogout={onLogout} />
      <main className="main-content">

        {/* TOP BAR */}
        <div className="att-top-bar">
          <div>
            <div className="att-page-title">Attendance &amp; Leave</div>
            <div className="att-page-subtitle">Biometric Tracking · Leave Management · Parent Notifications</div>
          </div>
          <div className="att-flex-center">
            <button className="att-btn att-btn-outline att-btn-sm" onClick={() => setLeaveModal(true)}>+ Apply Leave</button>
          </div>
        </div>

        {/* TABS */}
        <div className="att-tab-nav">
          {[
            { id:'today',         label:'📍 Today\'s Status' },
            { id:'biometric',     label:'🖐 Biometric Log' },
            { id:'leave',         label:'🏖 Leave Requests', badge: pendingCount },
            { id:'report',        label:'📊 Attendance Report' },
            { id:'notifications', label:'🔔 Notification Log' },
          ].map(tab => (
            <button key={tab.id}
              className={`att-tab-btn ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.id)}>
              {tab.label}
              {tab.badge > 0 && <span className="att-tab-badge">{tab.badge}</span>}
            </button>
          ))}
        </div>

        {/* ══ TODAY ══ */}
        {activeTab === 'today' && (
          <div className="att-body">
            {/* Cutoff Banner */}
            <div className="att-cutoff-banner">
              <div className="att-cutoff-info">
                <div className="att-cutoff-label">Nightly Cutoff — Auto Alerts Fire At</div>
                <div className="att-cutoff-time">10:00 PM</div>
                <div className="att-cutoff-desc">WhatsApp + Email auto-sent to all parents · {today}</div>
              </div>
              <div className="att-cutoff-divider" />
              <div className="att-cutoff-counters">
                <div className="att-cutoff-counter"><div className="att-cutoff-val">{overview.total}</div><div className="att-cutoff-clabel">Total</div></div>
                <div className="att-cutoff-counter"><div className="att-cutoff-val" style={{color:'#7EE8A2'}}>{overview.inPremises}</div><div className="att-cutoff-clabel">In Premises</div></div>
                <div className="att-cutoff-counter"><div className="att-cutoff-val" style={{color:'#FF8A80'}}>{overview.outside}</div><div className="att-cutoff-clabel">Outside</div></div>
                <div className="att-cutoff-counter"><div className="att-cutoff-val" style={{color:'#FFD54F'}}>{overview.onLeave}</div><div className="att-cutoff-clabel">On Leave</div></div>
              </div>
            </div>

            {/* Stats */}
            <div className="att-stats-grid">
              {[
                { color:'navy',  icon:'👥', val:overview.total,       label:'Total Students',  sub:'Tower A · Tower B' },
                { color:'green', icon:'✅', val:overview.inPremises,   label:'In Premises',     sub:`${overview.total>0?Math.round(overview.inPremises*100/overview.total):0}% present today` },
                { color:'red',   icon:'🚶', val:overview.outside,      label:'Outside',         sub:'No approved leave' },
                { color:'amber', icon:'🏖️',val:overview.onLeave,      label:'On Leave',         sub:'Approved & notified' },
                { color:'gold',  icon:'⏳', val:overview.pendingLeave, label:'Pending Leave',    sub:'Awaiting approval' },
              ].map(s => (
                <div key={s.label} className={`att-stat-card ${s.color}`}>
                  <span className="att-stat-icon">{s.icon}</span>
                  <div className="att-stat-value">{s.val}</div>
                  <div className="att-stat-label">{s.label}</div>
                  <div className="att-stat-sub">{s.sub}</div>
                </div>
              ))}
            </div>

            {loading && <div style={{padding:'10px 0 20px',color:'var(--att-muted)',fontSize:13,textAlign:'center'}}>Loading…</div>}
            {/* Live Table */}
            <div className="att-section">
              <div className="att-section-head">
                <div>
                  <div className="att-section-title">Live Premises Status</div>
                  <div className="att-section-meta">Last biometric sync: Today 7:42 PM · Next: 8:00 PM</div>
                </div>
                <div className="att-flex-center">
                  <button className="att-btn att-btn-ghost att-btn-sm">⬇️ Export</button>
                  <button className="att-btn att-btn-outline att-btn-sm">🔄 Refresh</button>
                </div>
              </div>
              <div className="att-filters">
                <div className="att-search-wrap">
                  <span className="att-search-icon">🔍</span>
                  <input className="att-search-input" placeholder="Search student…" value={search} onChange={e => setSearch(e.target.value)} />
                </div>
                <span className="att-filter-label">Status</span>
                <select className="att-filter-input" value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
                  <option value="">All</option>
                  <option value="in">In Premises</option>
                  <option value="out">Outside</option>
                  <option value="leave">On Leave</option>
                </select>
                <span className="att-filter-label">Tower</span>
                <select className="att-filter-input" value={towerFilter} onChange={e => setTowerFilter(e.target.value)}>
                  <option value="">All Towers</option>
                  <option value="Tower A">Tower A</option>
                  <option value="Tower B">Tower B</option>
                </select>
              </div>
              <table className="att-table">
                <thead>
                  <tr>
                    <th>Student</th><th>Student ID</th><th>Tower</th>
                    <th>Last Punch</th><th>Time</th><th>Status</th>
                    <th>Today's Punches</th><th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(s => (
                    <tr key={s.id}>
                      <td>
                        <div className="att-student-cell">
                          <Avatar name={s.name} size={34} />
                          <span className="att-student-name">{s.name}</span>
                        </div>
                      </td>
                      <td><span className="att-badge-tag">{s.id}</span></td>
                      <td>{s.tower}</td>
                      <td><PunchBadge punch={s.lastPunch} /></td>
                      <td>{s.lastTime}</td>
                      <td>
                        <StatusBadge status={s.status} />
                        {s.alert && <div className="att-alert-note">⚠ No Leave Applied</div>}
                      </td>
                      <td>
                        <div className="att-punch-row">
                          {s.punches.length > 0
                            ? s.punches.map((p, i) => (
                                <React.Fragment key={i}>
                                  {i > 0 && <span className="att-punch-arrow">›</span>}
                                  <span className={`att-punch-chip ${p.type}`}>
                                    {p.type === 'out' ? '↑' : '↓'} {p.time}
                                  </span>
                                </React.Fragment>
                              ))
                            : <span style={{fontSize:12,color:'var(--att-muted)'}}>{s.leaveInfo || '—'}</span>
                          }
                        </div>
                      </td>
                      <td>
                        <button className="att-btn att-btn-outline att-btn-sm" onClick={() => setDrawer(s)}>
                          View Details
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ══ BIOMETRIC LOG ══ */}
        {activeTab === 'biometric' && (
          <div className="att-body">
            <div className="att-section">
              <div className="att-section-head">
                <div>
                  <div className="att-section-title">Punch Log — Today</div>
                  <div className="att-section-meta">{today} · {bioLog.length} records</div>
                </div>
                <div className="att-flex-center">
                  <select className="att-filter-input" style={{fontSize:12}}><option>All Punches</option><option>Swipe IN</option><option>Swipe OUT</option></select>
                  <input type="date" className="att-filter-input" value={bioDate} onChange={e=>setBioDate(e.target.value)} style={{fontSize:12}} />
                  <button className="att-btn att-btn-ghost att-btn-sm">⬇️ Export</button>
                </div>
              </div>
              <div style={{padding:'10px 26px',background:'var(--att-cream)',borderBottom:'1px solid var(--att-light)',fontSize:13,color:'var(--att-muted)',display:'flex',alignItems:'center',gap:10}}>
                <span style={{fontSize:15}}>📌</span>
                <span><strong style={{color:'var(--att-text)'}}>Punch sequence logic:</strong> Punch 1 = OUT (student leaves for college) · Punch 2 = IN (returns to hostel) · alternating thereafter.</span>
              </div>
              <table className="att-table">
                <thead>
                  <tr><th>#</th><th>Student</th><th>Student ID</th><th>Device</th><th>Punch</th><th>Timestamp</th><th>Sequence</th><th>Status</th><th></th></tr>
                </thead>
                <tbody>
                  {bioLog.map((p, idx) => (
                    <tr key={p.id}>
                      <td style={{color:'var(--att-muted)'}}>{String(idx+1).padStart(3,'0')}</td>
                      <td><div className="att-student-cell"><Avatar name={p.studentName} size={30} /><span className="att-student-name">{p.studentName}</span></div></td>
                      <td><span className="att-badge-tag">{p.studentId}</span></td>
                      <td>{p.punchType==='OUT' ? <span className="att-badge att-badge-blue">OUT-Gate-1</span> : <span className="att-badge att-badge-approved">IN-Gate-1</span>}</td>
                      <td>{p.punchType==='OUT' ? '↑ Swipe OUT' : '↓ Swipe IN'}</td>
                      <td>{p.punchTime}</td>
                      <td><span className="att-badge att-badge-gold">Punch #{p.sequenceNo}</span></td>
                      <td>{p.punchType==='OUT' ? <span className="att-badge att-badge-out"><span className="att-badge-dot" />Left Premises</span> : <span className="att-badge att-badge-in"><span className="att-badge-dot" />Entered Premises</span>}</td>
                      <td><button className="att-btn att-btn-ghost att-btn-sm">View</button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ══ LEAVE ══ */}
        {activeTab === 'leave' && (
          <div className="att-body">
            <div style={{display:'grid',gridTemplateColumns:'1fr 340px',gap:22,alignItems:'start'}}>
              <div>
                <div style={{display:'flex',gap:10,alignItems:'center',marginBottom:16,flexWrap:'wrap'}}>
                  <div className="att-search-wrap"><span className="att-search-icon">🔍</span><input className="att-search-input" placeholder="Search student…" /></div>
                  <select className="att-filter-input"><option>All Status</option><option>Pending</option><option>Approved</option><option>Rejected</option></select>
                  <input type="date" className="att-filter-input" defaultValue={new Date().toISOString().slice(0,10)} />
                  <button className="att-btn att-btn-gold att-btn-sm" style={{marginLeft:'auto'}} onClick={() => setLeaveModal(true)}>+ New Leave Request</button>
                </div>

                {/* Pending */}
                {leaves.filter(l => l.status === 'pending').length > 0 && (
                  <>
                    <div style={{fontSize:11,fontWeight:700,textTransform:'uppercase',letterSpacing:'1.5px',color:'var(--att-muted)',marginBottom:10,paddingLeft:4}}>
                      Pending Approval — {leaves.filter(l => l.status === 'pending').length}
                    </div>
                    {leaves.filter(l => l.status === 'pending').map(lv => (
                      <LeaveCard key={lv.id} leave={lv} onApprove={() => updateLeave(lv.id,'approved')} onReject={() => updateLeave(lv.id,'rejected')} />
                    ))}
                  </>
                )}

                {/* Approved */}
                {leaves.filter(l => l.status === 'approved').length > 0 && (
                  <>
                    <div style={{fontSize:11,fontWeight:700,textTransform:'uppercase',letterSpacing:'1.5px',color:'var(--att-muted)',margin:'20px 0 10px',paddingLeft:4}}>
                      Approved — {leaves.filter(l => l.status === 'approved').length}
                    </div>
                    {leaves.filter(l => l.status === 'approved').map(lv => (
                      <LeaveCard key={lv.id} leave={lv} />
                    ))}
                  </>
                )}

                {/* Rejected */}
                {leaves.filter(l => l.status === 'rejected').length > 0 && (
                  <>
                    <div style={{fontSize:11,fontWeight:700,textTransform:'uppercase',letterSpacing:'1.5px',color:'var(--att-muted)',margin:'20px 0 10px',paddingLeft:4}}>
                      Rejected — {leaves.filter(l => l.status === 'rejected').length}
                    </div>
                    {leaves.filter(l => l.status === 'rejected').map(lv => (
                      <LeaveCard key={lv.id} leave={lv} />
                    ))}
                  </>
                )}
              </div>

              {/* Apply Panel */}
              <div className="att-section att-apply-panel">
                <div className="att-section-head">
                  <div><div className="att-section-title">Apply for Leave</div><div className="att-section-meta">Admin submission</div></div>
                </div>
                <div style={{padding:'18px 20px',display:'flex',flexDirection:'column',gap:12}}>
                  <div className="att-form-group" style={{position:'relative'}}>
                        <label className="att-form-label">Student ID / Name</label>
                        <input className="att-form-ctrl" placeholder="Type name or ID…"
                          value={studentSearch}
                          onChange={e => searchStudents(e.target.value)} />
                        {leaveForm.studentId && (
                          <div style={{fontSize:11,color:'var(--att-green)',marginTop:4,fontWeight:600}}>
                            ✓ Selected: {leaveForm.studentName}
                          </div>
                        )}
                        {studentSuggestions.length > 0 && (
                          <div style={{position:'absolute',top:'100%',left:0,right:0,background:'#fff',
                            border:'1px solid var(--att-light)',borderRadius:6,zIndex:999,
                            boxShadow:'0 4px 16px rgba(0,0,0,0.12)',maxHeight:200,overflowY:'auto'}}>
                            {studentSuggestions.map(s => (
                              <div key={s.studentId} onClick={() => selectStudent(s)}
                                style={{padding:'10px 14px',cursor:'pointer',fontSize:13,
                                  borderBottom:'1px solid var(--att-light)',display:'flex',gap:10,alignItems:'center'}}
                                onMouseEnter={e=>e.currentTarget.style.background='var(--att-cream)'}
                                onMouseLeave={e=>e.currentTarget.style.background='#fff'}>
                                <Avatar name={s.name} size={28} />
                                <div>
                                  <div style={{fontWeight:600}}>{s.name}</div>
                                  <div style={{fontSize:11,color:'var(--att-muted)'}}>{s.id} · {s.tower}</div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                  <div className="att-form-group"><label className="att-form-label">Leave Type</label>
                    <select className="att-form-ctrl" value={leaveForm.leaveType} onChange={e => setLeaveForm(p=>({...p,leaveType:e.target.value}))}>
                      <option value="HOME_LEAVE">Home Leave</option><option value="MEDICAL">Medical</option><option value="FESTIVAL">Festival / Holiday</option><option value="PERSONAL">Personal</option><option value="OTHER">Other</option>
                    </select>
                  </div>
                  <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10}}>
                    <div className="att-form-group"><label className="att-form-label">From</label><input type="date" className="att-form-ctrl" value={leaveForm.from} onChange={e => setLeaveForm(p=>({...p,from:e.target.value}))} /></div>
                    <div className="att-form-group"><label className="att-form-label">To</label><input type="date" className="att-form-ctrl" value={leaveForm.to} onChange={e => setLeaveForm(p=>({...p,to:e.target.value}))} /></div>
                  </div>
                  <div className="att-form-group"><label className="att-form-label">Reason</label><textarea className="att-form-ctrl" rows={3} placeholder="Purpose of leave…" value={leaveForm.reason} onChange={e => setLeaveForm(p=>({...p,reason:e.target.value}))} /></div>
                  <div className="att-form-group"><label className="att-form-label">Contact During Leave</label><input type="tel" className="att-form-ctrl" placeholder="Mobile number" value={leaveForm.contact} onChange={e => setLeaveForm(p=>({...p,contact:e.target.value}))} /></div>
                  <div className="att-form-group">
                    <label className="att-form-label">Notify Via</label>
                    <div style={{display:'flex',gap:12,marginTop:4,flexWrap:'wrap'}}>
                      <label style={{display:'flex',alignItems:'center',gap:6,fontSize:13,cursor:'pointer'}}><input type="checkbox" defaultChecked /> 📱 WhatsApp</label>
                      <label style={{display:'flex',alignItems:'center',gap:6,fontSize:13,cursor:'pointer'}}><input type="checkbox" defaultChecked /> 📧 Email</label>
                      <label style={{display:'flex',alignItems:'center',gap:6,fontSize:13,cursor:'pointer'}}><input type="checkbox" /> 📩 SMS</label>
                    </div>
                  </div>
                  <button className="att-btn att-btn-primary" style={{width:'100%',justifyContent:'center'}} onClick={submitLeave}>Submit Leave Request</button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ══ REPORT ══ */}
        {activeTab === 'report' && (
          <div className="att-body">
            <div className="att-section">
              <div className="att-section-head">
                <div><div className="att-section-title">Monthly Attendance Report</div><div className="att-section-meta">Each cell = 1 day</div></div>
                <div className="att-flex-center">
                  <input type="month" className="att-filter-input" value={reportMonth} onChange={e=>setReportMonth(e.target.value)} style={{fontSize:12}} />
                  <button className="att-btn att-btn-ghost att-btn-sm">⬇️ Export PDF</button>
                  <button className="att-btn att-btn-outline att-btn-sm">⬇️ Export Excel</button>
                </div>
              </div>
              <div style={{padding:'16px 26px',overflowX:'auto'}}>
                <div style={{display:'flex',gap:18,marginBottom:16,flexWrap:'wrap'}}>
                  {[{color:'var(--att-green)',label:'In Premises'},{color:'var(--att-red)',label:'Outside / Absent',opacity:0.7},{color:'var(--att-amber)',label:'On Leave'},{color:'var(--att-light)',label:'No Data'}].map(l=>(
                    <div key={l.label} style={{display:'flex',alignItems:'center',gap:7,fontSize:12,fontWeight:600}}>
                      <div style={{width:14,height:14,borderRadius:3,background:l.color,opacity:l.opacity||1}} />{l.label}
                    </div>
                  ))}
                </div>
                {/* Header row */}
                {monthlyReport.length > 0 && (() => {
                  const days = monthlyReport[0]?.heatmap?.length || 30;
                  return (
                    <div style={{display:'flex',gap:4,alignItems:'center',marginBottom:6}}>
                      <div style={{width:164,flexShrink:0}} />
                      <div style={{display:'flex',gap:3}}>
                        {Array.from({length:days},(_,i)=>i+1).map(d => <div key={d} style={{width:20,textAlign:'center',fontSize:9,fontWeight:700,color:'var(--att-muted)'}}>{d}</div>)}
                      </div>
                      <div style={{width:46,textAlign:'right',fontSize:9,fontWeight:700,color:'var(--att-muted)'}}>%</div>
                    </div>
                  );
                })()}
                {monthlyReport.map(s => {
                  const col = s.attendancePct >= 75 ? 'var(--att-green)' : 'var(--att-red)';
                  const short = s.studentName ? s.studentName.split(' ') : ['?'];
                  return (
                    <div key={s.studentId} className="att-heatmap-row">
                      <div className="att-heatmap-name-cell"><Avatar name={s.studentName} size={26} /><span className="att-heatmap-name">{short[0]} {short[1]?.[0] || ''}.</span></div>
                      <div className="att-heatmap-cells">
                        {(s.heatmap || []).map((cell, i) => {
                          const st = cell.status === 'IN' ? 'in' : cell.status === 'LEAVE' ? 'leave' : cell.status === 'OUT' ? 'out' : 'none';
                          return <div key={i} className={`att-hm-cell att-hm-${st}`} title={`${s.studentName} · Day ${cell.day}: ${cell.status}`} />;
                        })}
                      </div>
                      <div className="att-heatmap-pct" style={{color:col}}>{s.attendancePct}%</div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Defaulters */}
            <div className="att-section">
              <div className="att-section-head">
                <div><div className="att-section-title">Low Attendance — Below 75%</div><div className="att-section-meta">Auto WhatsApp fires at nightly cutoff</div></div>
              </div>
              <table className="att-table">
                <thead><tr><th>Student</th><th>Student ID</th><th>Tower</th><th>Present</th><th>Absent</th><th>On Leave</th><th>Attendance %</th><th>Action</th></tr></thead>
                <tbody>
                  {monthlyReport.filter(s => s.attendancePct < 75).map(s => {
                    const col = s.attendancePct < 65 ? 'var(--att-red)' : 'var(--att-amber)';
                    return (
                      <tr key={s.studentId}>
                        <td><div className="att-student-cell"><Avatar name={s.studentName} size={32} /><span className="att-student-name">{s.studentName}</span></div></td>
                        <td><span className="att-badge-tag">{s.admissionNumber}</span></td>
                        <td>{s.tower}</td>
                        <td>{s.presentDays}</td>
                        <td style={{color:'var(--att-red)',fontWeight:700}}>{s.absentDays}</td>
                        <td>{s.leaveDays}</td>
                        <td>
                          <div style={{display:'flex',alignItems:'center',gap:10}}>
                            <div style={{background:'var(--att-light)',borderRadius:4,height:8,width:76,overflow:'hidden'}}>
                              <div style={{background:col,height:'100%',width:`${s.attendancePct}%`,borderRadius:4}} />
                            </div>
                            <strong style={{color:col}}>{s.attendancePct}%</strong>
                          </div>
                        </td>
                        <td>
                          <div className="att-flex-center">
                            <button className="att-btn att-btn-ghost att-btn-sm">View</button>
                            <button className="att-btn att-btn-sm" style={{background:'var(--att-amberl)',color:'var(--att-amber)',border:'1px solid #FFE082'}}>📲 Alert</button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ══ NOTIFICATIONS ══ */}
        {activeTab === 'notifications' && (
          <div className="att-body">
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:16,marginBottom:22}}>
              {[
                { color:'green', icon:'📱', val:318, label:'WhatsApp Sent Today',  sub:'All delivered' },
                { color:'navy',  icon:'📧', val:142, label:'Emails Sent',           sub:'Cutoff alert batch' },
                { color:'amber', icon:'⏳', val:6,   label:'Pending / Failed',       sub:'Retry in progress' },
              ].map(s => (
                <div key={s.label} className={`att-stat-card ${s.color}`}>
                  <span className="att-stat-icon">{s.icon}</span>
                  <div className="att-stat-value">{s.val}</div>
                  <div className="att-stat-label">{s.label}</div>
                  <div className="att-stat-sub">{s.sub}</div>
                </div>
              ))}
            </div>

            <div className="att-section">
              <div className="att-section-head">
                <div><div className="att-section-title">Notification Log</div><div className="att-section-meta">{today} · All auto-triggered</div></div>
                <div className="att-flex-center">
                  <select className="att-filter-input" style={{fontSize:12}}><option>All Types</option><option>Cutoff Alert</option><option>Leave Approval</option><option>Leave Rejection</option><option>Attendance Report</option></select>
                  <select className="att-filter-input" style={{fontSize:12}}><option>All Channels</option><option>WhatsApp</option><option>Email</option><option>SMS</option></select>
                </div>
              </div>
              {[
                { icon:'📱', wrap:'att-notif-wa',    status:'sent',    statusLabel:'✓ Delivered', text: <><strong>WhatsApp · Cutoff Alert</strong> — Parent of <strong>Priya Sharma (OHM-2024-078)</strong>:<br />"Your ward is currently outside hostel premises as of 10:00 PM. Please confirm her whereabouts. — Omkara Hostel."</>, time:'Today 10:01 PM · Twilio WhatsApp · Auto-triggered' },
                { icon:'📱', wrap:'att-notif-wa',    status:'pending', statusLabel:'⏳ Awaiting Reply', text: <><strong>WhatsApp · Leave Request</strong> — Parent of <strong>Arjun Kulkarni (OHM-2024-041)</strong>:<br />"Arjun has applied for home leave 13–14 Apr (Gudi Padwa). Reply YES to approve or NO to reject. — Omkara Hostel."</>, time:'Today 10:35 AM · Twilio WhatsApp · Auto-triggered' },
                { icon:'📱', wrap:'att-notif-wa',    status:'sent',    statusLabel:'✓ Delivered', text: <><strong>WhatsApp · Leave Approved</strong> — Parent &amp; student <strong>Rahul Mehta (OHM-2023-019)</strong>:<br />"Leave approved for 10–14 Apr. Must report back by 14 Apr 10 PM. Safe travels! — Omkara Hostel."</>, time:'9 Apr 3:22 PM · Twilio WhatsApp · Auto-triggered' },
                { icon:'📧', wrap:'att-notif-email', status:'sent',    statusLabel:'✓ 138/142 Delivered', text: <><strong>Email · Nightly Attendance Report</strong> — Batch to admin &amp; 142 parent addresses. Summary: 118 in premises, 16 outside, 8 on leave.</>, time:'Today 10:02 PM · SendGrid · Auto-triggered' },
                { icon:'📩', wrap:'att-notif-sms',   status:'sent',    statusLabel:'✓ Delivered', text: <><strong>SMS · Low Attendance Warning</strong> — Parent of <strong>Vikram Patil (OHM-2024-055)</strong>:<br />"Attendance alert: Vikram Patil's hostel attendance is 58%. Please contact admin. — Omkara Hostel."</>, time:'Today 9:00 AM · MSG91 · Auto-triggered' },
              ].map((n, i) => (
                <div key={i} className="att-notif-item">
                  <div className={`att-notif-icon-wrap ${n.wrap}`}>{n.icon}</div>
                  <div className="att-notif-body">
                    <div className="att-notif-text">{n.text}</div>
                    <div style={{display:'flex',alignItems:'center',gap:10,marginTop:6}}>
                      <div className="att-notif-time">{n.time}</div>
                      <span className={n.status === 'sent' ? 'att-notif-sent' : 'att-notif-pending'}>{n.statusLabel}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ══ DRAWER ══ */}
        <div className={`att-drawer-overlay ${drawer ? 'open' : ''}`} onClick={() => setDrawer(null)} />
        <div className={`att-drawer ${drawer ? 'open' : ''}`}>
          {drawer && (
            <>
              <div className="att-drawer-head">
                <Avatar name={drawer.name} size={52} />
                <div>
                  <div className="att-drawer-name">{drawer.name}</div>
                  <div className="att-drawer-id">{drawer.id} · {drawer.tower}</div>
                </div>
                <button className="att-drawer-close" onClick={() => setDrawer(null)}>✕</button>
              </div>
              <div className="att-drawer-body">
                <div className="att-drawer-section">
                  <div className="att-drawer-section-title">Today's Summary</div>
                  <div className="att-info-grid">
                    <div><div className="att-info-label">Status</div><div className="att-info-val"><StatusBadge status={drawer.status} /></div></div>
                    <div><div className="att-info-label">Tower</div><div className="att-info-val">{drawer.tower}</div></div>
                    <div><div className="att-info-label">Last Punch</div><div className="att-info-val">{drawer.lastPunch === '—' ? '— (On Leave)' : `${drawer.lastPunch} at ${drawer.lastTime}`}</div></div>
                    <div>
                      <div className="att-info-label">Monthly %</div>
                      <div className="att-info-val" style={{color: drawer.attendance >= 75 ? 'var(--att-green)' : 'var(--att-red)'}}>
                        <strong>{drawer.attendance}%</strong> this month
                      </div>
                    </div>
                  </div>
                </div>
                <div className="att-drawer-section">
                  <div className="att-drawer-section-title">Today's Punch Timeline</div>
                  <div className="att-punch-timeline">
                    {drawer.punches.length > 0
                      ? drawer.punches.map((p, i) => (
                          <div key={i} className="att-timeline-item">
                            <div className="att-timeline-icon">{p.type === 'out' ? '🚪' : '🏠'}</div>
                            <div>
                              <div className="att-timeline-label">{p.type === 'out' ? 'Left Premises' : 'Entered Premises'}</div>
                              <div className="att-timeline-time">Punch #{p.seq} · {p.time}</div>
                            </div>
                            <div style={{marginLeft:'auto'}}>
                              {p.type === 'out'
                                ? <span className="att-badge att-badge-out"><span className="att-badge-dot" />OUT</span>
                                : <span className="att-badge att-badge-in"><span className="att-badge-dot" />IN</span>}
                            </div>
                          </div>
                        ))
                      : drawer.leaveFlag
                        ? <div style={{padding:12,background:'var(--att-amberl)',borderRadius:8,fontSize:13,color:'var(--att-amber)',fontWeight:600}}>🏖 {drawer.leaveInfo || 'On approved leave'}</div>
                        : <div style={{color:'var(--att-muted)',fontSize:13,padding:'6px 0'}}>No punches recorded today.</div>
                    }
                  </div>
                </div>
                <div className="att-drawer-section">
                  <div className="att-drawer-section-title">This Week</div>
                  <div className="att-week-grid">
                    {['Mon','Tue','Wed','Thu','Fri','Sat','Sun'].map((day, i) => (
                      <div key={day} className="att-week-cell">
                        <div className="att-week-day">{day}</div>
                        <div className={`att-week-dot att-wc-${drawer.thisWeek?.[i] || 'none'}`} />
                      </div>
                    ))}
                  </div>
                </div>
                <div className="att-drawer-section">
                  <div className="att-drawer-section-title">Quick Actions</div>
                  <div style={{display:'flex',gap:9,flexWrap:'wrap'}}>
                    <button className="att-btn att-btn-outline att-btn-sm" onClick={() => { setDrawer(null); setLeaveModal(true); }}>📝 Apply Leave</button>
                    <button className="att-btn att-btn-ghost att-btn-sm">📲 Message Parent</button>
                    <button className="att-btn att-btn-ghost att-btn-sm">📋 Full Report</button>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>

        {/* ══ LEAVE MODAL ══ */}
        <div className={`att-modal-overlay ${leaveModal ? 'open' : ''}`} onClick={e => { if(e.target.className.includes('att-modal-overlay')) setLeaveModal(false); }}>
          <div className="att-modal">
            <div className="att-modal-head">
              <div className="att-modal-title">Leave Application</div>
              <button className="att-modal-close" onClick={() => setLeaveModal(false)}>✕</button>
            </div>
            <div className="att-form-grid">
              <div className="att-form-group" style={{position:'relative'}}>
                        <label className="att-form-label">Student ID / Name</label>
                        <input className="att-form-ctrl" placeholder="Type name or ID…"
                          value={studentSearch}
                          onChange={e => searchStudents(e.target.value)} />
                        {leaveForm.studentId && (
                          <div style={{fontSize:11,color:'var(--att-green)',marginTop:4,fontWeight:600}}>
                            ✓ Selected: {leaveForm.studentName}
                          </div>
                        )}
                        {studentSuggestions.length > 0 && (
                          <div style={{position:'absolute',top:'100%',left:0,right:0,background:'#fff',
                            border:'1px solid var(--att-light)',borderRadius:6,zIndex:999,
                            boxShadow:'0 4px 16px rgba(0,0,0,0.12)',maxHeight:200,overflowY:'auto'}}>
                            {studentSuggestions.map(s => (
                              <div key={s.studentId} onClick={() => selectStudent(s)}
                                style={{padding:'10px 14px',cursor:'pointer',fontSize:13,
                                  borderBottom:'1px solid var(--att-light)',display:'flex',gap:10,alignItems:'center'}}
                                onMouseEnter={e=>e.currentTarget.style.background='var(--att-cream)'}
                                onMouseLeave={e=>e.currentTarget.style.background='#fff'}>
                                <Avatar name={s.name} size={28} />
                                <div>
                                  <div style={{fontWeight:600}}>{s.name}</div>
                                  <div style={{fontSize:11,color:'var(--att-muted)'}}>{s.id} · {s.tower}</div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
              <div className="att-form-group"><label className="att-form-label">Leave Type</label>
                <select className="att-form-ctrl" value={leaveForm.leaveType} onChange={e=>setLeaveForm(p=>({...p,leaveType:e.target.value}))}>
                  <option value="HOME_LEAVE">Home Leave</option><option value="MEDICAL">Medical</option><option value="FESTIVAL">Festival / Holiday</option><option value="PERSONAL">Personal</option><option value="OTHER">Other</option>
                </select>
              </div>
              <div className="att-form-group"><label className="att-form-label">From Date</label><input type="date" className="att-form-ctrl" value={leaveForm.from} onChange={e=>setLeaveForm(p=>({...p,from:e.target.value}))} /></div>
              <div className="att-form-group"><label className="att-form-label">To Date</label><input type="date" className="att-form-ctrl" value={leaveForm.to} onChange={e=>setLeaveForm(p=>({...p,to:e.target.value}))} /></div>
              <div className={`att-form-group att-form-group-full`}><label className="att-form-label">Reason</label><textarea className="att-form-ctrl" rows={3} placeholder="Purpose of leave…" value={leaveForm.reason} onChange={e=>setLeaveForm(p=>({...p,reason:e.target.value}))} /></div>
              <div className="att-form-group"><label className="att-form-label">Destination</label><input className="att-form-ctrl" placeholder="Where will the student be?" value={leaveForm.destination} onChange={e=>setLeaveForm(p=>({...p,destination:e.target.value}))} /></div>
              <div className="att-form-group"><label className="att-form-label">Contact During Leave</label><input type="tel" className="att-form-ctrl" placeholder="Mobile number" value={leaveForm.contact} onChange={e=>setLeaveForm(p=>({...p,contact:e.target.value}))} /></div>
              <div className={`att-form-group att-form-group-full`}>
                <label className="att-form-label">Notify Via</label>
                <div style={{display:'flex',gap:18,marginTop:4}}>
                  <label style={{display:'flex',alignItems:'center',gap:7,fontSize:13,cursor:'pointer'}}><input type="checkbox" defaultChecked /> 📱 WhatsApp (Parent)</label>
                  <label style={{display:'flex',alignItems:'center',gap:7,fontSize:13,cursor:'pointer'}}><input type="checkbox" defaultChecked /> 📧 Email</label>
                  <label style={{display:'flex',alignItems:'center',gap:7,fontSize:13,cursor:'pointer'}}><input type="checkbox" /> 📩 SMS</label>
                </div>
              </div>
            </div>
            <div className="att-form-footer">
              <button className="att-btn att-btn-ghost" onClick={() => setLeaveModal(false)}>Cancel</button>
              <button className="att-btn att-btn-primary" onClick={submitLeave}>Submit Leave Request</button>
            </div>
          </div>
        </div>

      </main>
    </div>
  );
};

/* ─── Leave Card Component ─── */
const LeaveCard = ({ leave, onApprove, onReject }) => (
  <div className={`att-leave-card ${leave.status}`}>
    <Avatar name={leave.name} size={44} />
    <div className="att-leave-info">
      <div style={{display:'flex',alignItems:'center',gap:9,flexWrap:'wrap'}}>
        <span className="att-leave-name">{leave.name}</span>
        <span className="att-badge-tag">{leave.studentId}</span>
        {leave.status === 'pending'  && <span className="att-badge att-badge-pending">Pending</span>}
        {leave.status === 'approved' && <span className="att-badge att-badge-approved">✓ Approved</span>}
        {leave.status === 'rejected' && <span className="att-badge att-badge-rejected">✗ Rejected</span>}
      </div>
      <div className="att-leave-meta">{leave.tower} · Applied: {leave.applied}</div>
      <div className="att-leave-reason">{leave.reason}</div>
      <div className="att-leave-dates">
        <div className="att-leave-date-item"><div className="att-leave-date-label">From</div><div className="att-leave-date-val">{leave.from}</div></div>
        <div className="att-leave-date-item"><div className="att-leave-date-label">To</div><div className="att-leave-date-val">{leave.to}</div></div>
        <div className="att-leave-date-item"><div className="att-leave-date-label">Days</div><div className="att-leave-date-val">{leave.days}</div></div>
        <div className="att-leave-date-item"><div className="att-leave-date-label">Type</div><div className="att-leave-date-val">{leave.type}</div></div>
      </div>
    </div>
    <div className="att-leave-actions">
      {leave.status === 'pending' && (
        <>
          {leave.parentStatus === 'awaiting' && <span className="att-badge att-badge-gold" style={{fontSize:10}}>⏳ Awaiting Parent</span>}
          {leave.parentStatus === 'notified' && <span className="att-badge att-badge-blue" style={{fontSize:10}}>📱 Parent Notified</span>}
          {can('APPROVE_LEAVES') && <button className="att-btn-approve" onClick={onApprove}>✓ Approve</button>}
          {can('APPROVE_LEAVES') && <button className="att-btn-reject"  onClick={onReject}>✗ Reject</button>}
        </>
      )}
      {leave.status === 'approved' && (
        <>
          <span className="att-badge att-badge-approved">✓ Approved</span>
          <span style={{fontSize:11,color:'var(--att-muted)'}}>WhatsApp sent ✓</span>
        </>
      )}
      {leave.status === 'rejected' && (
        <>
          <span className="att-badge att-badge-rejected">✗ Rejected</span>
          <span style={{fontSize:11,color:'var(--att-muted)'}}>Parent notified</span>
        </>
      )}
    </div>
  </div>
);

export default AttendanceScreen;